import React, { useState, useEffect } from "react";
import { CheckCircle2, Clock, Play, AlertCircle, XCircle } from "lucide-react";
import ProspectPropertyService from "../../Service/ProspectPropertyService";

const iconsMap = {
  "Info Collection & Assignment": <AlertCircle className="w-5 h-5" />,
  Analysis: <CheckCircle2 className="w-5 h-5" />,
  "Presentation Preparation": <Clock className="w-5 h-5" />,
  "Confirmed List": <Play className="w-5 h-5" />,
  Dropped: <XCircle className="w-5 h-5 text-red-500" />,
};

const PropertyProgress = ({
  currentStep: propCurrentStep,
  setCurrentStep: propSetCurrentStep,
  propertyId,
  setCurrentStage,
  setProgressTrigger,
  progressTrigger
}) => {
  const [internalCurrentStep, setInternalCurrentStep] = useState(1);
  const currentStep = propCurrentStep || internalCurrentStep;
  const setCurrentStep = propSetCurrentStep || setInternalCurrentStep;

  const [steps, setSteps] = useState([]);
  const [isDropped, setIsDropped] = useState(false);
  const [currentStageStep, setCurrentStageStep] = useState(1); // Step corresponding to currentStatus

  useEffect(() => {
    const fetchStatusDetails = async () => {
      try {
        const response = await ProspectPropertyService.getStatusDetails(propertyId);
        const data = response.data?.data;

        if (data?.statusHistory?.length) {
          const mappedSteps = data.statusHistory.map((item, index) => ({
            id: index + 1,
            label: item.status,
            date:
              item.startDate && item.startDate !== "TBD"
                ? `${item.startDate} - ${item.endDate || "TBD"}`
                : "TBD",
            days: item.dayCount > 0 ? `${item.dayCount} Days` : null,
            progress: Math.min((index + 1) * (100 / data.statusHistory.length), 100),
            icon: iconsMap[item.status] || <Clock className="w-5 h-5" />,
          }));

          setSteps(mappedSteps);

         
          if (data.currentStatus === "Dropped") setIsDropped(true);

          // set current stage in parent
          setCurrentStage(data.currentStatus);

          // Find index of current status
          const activeStepIndex = mappedSteps.findIndex(
            (s) => s.label === data.currentStatus
          );
          if (activeStepIndex !== -1) {
            setCurrentStep(activeStepIndex + 1);
            setCurrentStageStep(activeStepIndex + 1); // this controls the progress bar
          }
        }
      } catch (error) {
        console.error("Error fetching property status details:", error);
      }
    };
    
    if (propertyId) {
      fetchStatusDetails();
    }
  }, [propertyId, progressTrigger]); 
 
  const getLineProgressPercentage = () => {
    if (!steps.length) return 0;
    return ((currentStageStep - 1) / (steps.length - 1)) * 100;
  };

  const handleStepClick = (stepId) => {
    setCurrentStep(stepId); 
  };



const getStepStatus = (step) => {
  if (step.id < currentStageStep && step.id !== currentStep) return "completed"; // green
  if (step.id === currentStep) return "active"; // orange
  return "upcoming"; // gray
};


  return (
    <div className="bg-white rounded-2xl w-full p-8 shadow-lg border backdrop-blur-sm">
      {steps.length > 0 ? (
        <div className="relative ">
         
          <div className="absolute top-4 left-[90px] right-[30px] h-4 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ease-out ${
                isDropped ? "bg-red-500" : "bg-[#B7E5BA]"
              }`}
              style={{ width: `${getLineProgressPercentage()}%` }}
            />
          </div>

          {/* Steps */}
          <div className="flex justify-between items-start relative z-10">
            {steps.map((step) => {
              const status = getStepStatus(step);
              return (
                <div
                  key={step.id}
                  className="flex flex-col items-center text-center max-w-[200px] group cursor-pointer transform transition-all duration-300"
                  onClick={() => handleStepClick(step.id)}
                >
                  <div className="relative mb-4 w-12 h-12">
                    <div
                      className={`absolute inset-0 rounded-full shadow-lg transition-all duration-300 ${
                        status === "completed"
                          ? "bg-white border-2 border-customGreen"
                          : status === "active"
                          ? "bg-white border-2 border-orange-200"
                          : isDropped && step.id === currentStep
                          ? "bg-white border-2 border-red-500"
                          : "bg-white border-2 border-gray-200"
                      }`}
                    />
                  </div>

                  <div
                    className={`transition-all duration-300 ${
                      status === "active" ? "transform scale-105" : ""
                    }`}
                  >
                    <div
                      className={`text-sm font-bold text-wrap leading-tight mb-2 ${
                        isDropped && step.id === currentStep
                          ? "text-red-700"
                          : status === "active"
                          ? "text-orange-700"
                          : status === "completed"
                          ? "text-green-700"
                          : "text-gray-600"
                      }`}
                    >
                      {step.label}
                    </div>
                    <div
                      className={`text-xs mb-2 font-medium ${
                        isDropped && step.id === currentStep
                          ? "text-red-600"
                          : status === "active"
                          ? "text-orange-600"
                          : "text-gray-500"
                      }`}
                    >
                      {step.date}
                    </div>
                    {step.id === currentStep && step.days && (
                      <div
                        className={`inline-flex items-center text-xs font-semibold ${
                          isDropped
                            ? "text-red-700 bg-red-100 border border-red-200"
                            : "text-orange-700 bg-gradient-to-r from-orange-100 to-orange-200 border border-orange-200"
                        } px-3 py-1.5 rounded-full shadow-sm`}
                      >
                        <Clock className="w-3 h-3 mr-1" />
                        {step.days}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-center text-gray-500">Loading progress...</div>
      )}
    </div>
  );
};



export default PropertyProgress;
